import { useState, useEffect } from "react";
import { scoreTown } from "../lib/scoring.js";
import { hardCriteriaCheck, countPass } from "../lib/hardCriteria.js";
import { VIBE_THEMES } from "../lib/criteria.js";
import { prettyConcelho } from "../lib/localization.js";
import { fetchWikiThumb } from "../lib/wiki.js";
import { curatedPhoto } from "../lib/placePhotos.js";

// Eén plaats-kaart in het home-raster.
export function PlaceCard({ town, concelho, weights, isFav, onToggleFav, onOpen, canEdit = true, inCompare = false, onToggleCompare, enforceVeto = false, waterVeto = false }) {
  const { pct, excluded } = scoreTown(town, weights, concelho, enforceVeto, { waterVeto });
  const rounded = Math.round(pct);
  const items   = hardCriteriaCheck(town, concelho);
  const pass    = countPass(items);
  const total   = items.length;
  const theme   = VIBE_THEMES[town.vibe?.theme] || VIBE_THEMES.atlantic;
  const cl      = concelho?.climate || {};

  const matchLabel = rounded >= 70 ? "sterke match" : rounded >= 50 ? "redelijke match" : "matig";
  const matchOk    = pass >= total - 1;
  const sea        = town.hardRequirements?.seaDistanceMin;

  // Actuele foto van de plek (Wikipedia), gradient als fallback.
  const [photo, setPhoto] = useState(() => curatedPhoto(town.concelho));
  useEffect(() => {
    let alive = true;
    const fixed = curatedPhoto(town.concelho);
    if (fixed) { setPhoto(fixed); return; }
    const page = town.vibe?.wikiPage;
    if (page) fetchWikiThumb(page).then(url => { if (alive && url) setPhoto(url); });
    return () => { alive = false; };
  }, [town.concelho, town.vibe?.wikiPage]);

  return (
    <div className="pc-card" style={excluded ? { opacity:0.72 } : undefined} onClick={() => onOpen(town)} role="button" tabIndex={0}
         aria-label={`${town.name} — open details`}
         onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(town); } }}>
      <div className="pc-hero" style={{ background: theme.gradient }}>
        {photo && <div className="pc-photo" style={{ backgroundImage:`url("${photo}")` }} />}
        {!photo && town.vibe?.wikiPage && <div className="pc-skeleton" aria-hidden="true" />}
        {!photo && <span className="pc-emoji">{town.vibe?.emoji || "📍"}</span>}
        <span className="pc-score-badge" style={excluded ? { color:"#a32d2d" } : undefined}>{excluded ? `${rounded} · uitgesloten` : `${rounded} · ${matchLabel}`}</span>
        {onToggleCompare && (
          <button
            className={`pc-cmp${inCompare ? " pc-cmp-on" : ""}`}
            aria-label={inCompare ? "Uit vergelijking halen" : "Aan vergelijking toevoegen"}
            title="Vergelijken"
            onClick={e => { e.stopPropagation(); onToggleCompare(town.name); }}
          >⚖{inCompare ? " ✓" : ""}</button>
        )}
        {canEdit
          ? <button
              className="pc-fav"
              aria-label={isFav ? "Verwijder uit bewaard" : "Bewaar plek"}
              onClick={e => { e.stopPropagation(); onToggleFav(town.name); }}
            >{isFav ? "★" : "☆"}</button>
          : (isFav && <span className="pc-fav" aria-hidden="true">★</span>)}
      </div>
      <div className="pc-body">
        <div className="pc-head">
          <span className="pc-name">{town.name}</span>
          <span className="pc-eisen" style={{ color: matchOk ? "#3b7a35" : "#c17f2a" }}>
            {matchOk ? "✓" : "⚠"} {pass}/{total} eisen
          </span>
        </div>
        <p className="pc-meta">{town.region} · {prettyConcelho(town.concelho)}{sea != null ? ` · ${sea} min zee` : ""}</p>
        <p className="pc-climate">
          {cl.annual_rainfall_mm != null && <span>🌧 {cl.annual_rainfall_mm} mm</span>}
          {cl.annual_sunshine_h != null && <span> · ☀ {cl.annual_sunshine_h} h</span>}
        </p>
      </div>
    </div>
  );
}
